// Telefonie SP7 (A13): „Neuer Vorgang" aus einem Anruf.
//
// Öffnet den zweistufigen Vorgang-Erstell-Flow (VorgangCreateFlowWindow) mit
// Kontakt-Vorbelegung, Betreff „Anruf vom <Datum>" und einer Erst-Notiz mit
// Richtung/Datum/Uhrzeit/Nummer des Anrufs. Nach dem Anlegen wird der Anruf an
// den neuen Vorgang gebunden UND automatisch als erledigt markiert — exakt das
// Mail-Muster aus MailDetailWindow (dort MailLink; hier CallLog.vorgangId).
//
// Entscheidungen (docs/plan-review-2026-07-05-entscheidungen.md):
//   55 Vorgangsart „Rückruf" existiert — die Art wählt der User aber im Flow;
//      dieser Einstieg belegt sie NICHT vor (kein Zwang, Entscheidung 51).
//   59 Vorgang aus Anruf → Anruf auto-erledigen (resolved: true).
//   60 Erledigt-Recht: jeder Sichtberechtigte (Backend-Guard prüft das).
import {
    openVorgangCreateFlow,
    type VorgangCreateResult,
} from "../vorgang/create/VorgangCreateFlowWindow";
import { telephonyService, type CallLogDto } from "../../services/telephonyService";
import { kontaktAbgleichAnbieten } from "./kontaktInVorgangAbgleich";
import { DIRECTION_LABEL, counterpartyNumber, fmtDate, fmtTime } from "./telephonyUtils";

/** `openWindow` aus dem WindowManager (Signatur von openVorgangCreateFlow übernommen). */
type OpenWindowFn = Parameters<typeof openVorgangCreateFlow>[0];

/** Betreff-Vorbelegung: „Anruf vom <Datum>". */
export function callVorgangSubject(call: CallLogDto): string {
    return `Anruf vom ${fmtDate(call.startedAt)}`;
}

/**
 * Erst-Notiz aus dem Anruf-Kontext: Richtung + Datum/Uhrzeit + Nummer (sofern
 * bekannt). Wird nach dem Anlegen als erste Notiz in den Aufgaben-Chat gepostet.
 */
export function callVorgangNote(call: CallLogDto): string {
    const num = counterpartyNumber(call);
    const lines = [
        `${DIRECTION_LABEL[call.direction]}er Anruf am ${fmtDate(call.startedAt)} um ${fmtTime(call.startedAt)} Uhr`,
        num ? `Nummer: ${num}` : null,
    ].filter((l): l is string => !!l);
    return lines.join("\n");
}

/**
 * Öffnet den Vorgang-Erstell-Flow für einen Anruf. `onChanged` erhält das
 * aktualisierte Anruf-DTO (verknüpft + erledigt), damit Liste/Detail sofort
 * nachziehen. Fehler beim Binden/Erledigen dürfen den bereits angelegten
 * Vorgang NICHT als Fehlschlag melden (fehlertolerant, nur Logging).
 */
export function openVorgangFromCall(
    openWindow: OpenWindowFn,
    call: CallLogDto,
    onChanged?: (next: CallLogDto) => void,
): void {
    openVorgangCreateFlow(openWindow, {
        initialContactId: call.contactId ?? null,
        initialContactName: call.contactDisplayName ?? null,
        initialSubject: callVorgangSubject(call),
        initialNote: callVorgangNote(call),
        onCreated: async (result: VorgangCreateResult) => {
            try {
                // Anruf ZUSÄTZLICH zuordnen und erledigen (Entscheidung 59,
                // PO 01.10.2026 F1): Hängt der Anruf schon an anderen
                // Vorgängen, bleiben die stehen — „Neuer Vorgang (zusätzlich)".
                // Ersetzen heißt: die alte Zuordnung im Detail per × lösen.
                // Der Server stempelt nur einen OFFENEN Anruf als erledigt.
                if (!result.vorgangId) {
                    if (!call.resolvedAt) onChanged?.(await telephonyService.updateCall(call.id, { resolved: true }));
                    return;
                }
                const updated = await telephonyService.addZuordnungen(
                    call.id,
                    [{ vorgangId: result.vorgangId, todoId: null }],
                    { resolve: true },
                );
                kontaktAbgleichAnbieten(updated.fehlendeKontakte);
                onChanged?.(updated);
            } catch (e) {
                console.error("[Telefonie] Anruf↔Vorgang-Verknüpfung fehlgeschlagen", e);
            }
        },
    });
}
